import { inject, Injectable } from '@angular/core';
import { UsersService } from 'app/features/user-management/services/users.service';
import { Site } from 'app/shared/types';
import { ContextService } from 'core/services/context.service';
import { combineLatest } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { Role } from 'shared/constants/role';
import { RolesEnum } from 'shared/enums';
import { EnumHelper } from 'shared/helpers';
import { UserInformation } from 'shared/types/user/user-information';

@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  public authUserService = inject(UsersService);
  public contextService = inject(ContextService);
  public userInfo?: UserInformation;
  public loggedInUser$ = this.authUserService.getLoggedInUserWithRoles().pipe(
    map(({ value }) => {
      this.userInfo = value;
      this.contextService.userInfo.next(this.userInfo);
      return value;
    }),
    shareReplay(1)
  );
  public isSuperAdmin$ = this.loggedInUser$.pipe(map((loggedInUser) => loggedInUser.isSuperAdmin));
  public canAccessSiteManagement$ = combineLatest([
    this.loggedInUser$,
    this.contextService.siteId.asObservable(),
    this.isSuperAdmin$,
  ]).pipe(
    map(([userInfo, siteId, isSuperAdmin]) => isSuperAdmin || this.checkSiteRoles([Role.SiteAdmin], siteId, userInfo))
  );
  private userRolesList = EnumHelper.toValueArray(RolesEnum);

  constructor() {
    if (this.contextService.userInfo.value) {
      this.contextService.userInfo.asObservable().subscribe((userInfo) => {
        if (userInfo) {
          this.userInfo = userInfo;
        }
      });
    }
  }

  public checkSiteRoles(
    roleIds: number[],
    siteId: string | null = this.contextService.siteId.value,
    userInfo = this.userInfo
  ): boolean {
    if (!userInfo) {
      return false;
    }
    if (userInfo.isSuperAdmin) {
      return true;
    }
    if (!siteId) {
      return false;
    }
    return userInfo.userSites.some((userSite) => {
      const roleMatch = roleIds.includes(userSite.roleId);
      const siteMatch = siteId === userSite.site?.id;
      const result = roleMatch && siteMatch;
      return result;
    });
  }

  public hasSiteRoles(
    roleIds: number[],
    siteId: string | null = this.contextService.siteId.value,
    userInfo = this.userInfo
  ): boolean {
    if (!userInfo) {
      return false;
    }
    if (!siteId) {
      return false;
    }
    return userInfo.userSites.some((userSite) => {
      const roleMatch = roleIds.includes(userSite.roleId);
      const siteMatch = siteId === userSite.site?.id;
      return roleMatch && siteMatch;
    });
  }

  public canAccessSiteManagement(siteId: string, userInfo = this.userInfo): boolean {
    return this.isSuperAdmin(userInfo) || this.checkSiteRoles([Role.SiteAdmin], siteId, userInfo);
  }

  public isSuperAdmin(userInfo = this.userInfo): boolean {
    if (!userInfo) return false;
    return userInfo.isSuperAdmin;
  }

  public getSitesByClient(id: string): Site[] {
    return this.userInfo?.clients.find((client) => client.id === id)?.sites || [];
  }

  public getSitesByClientAndRole(id: string, role: number): Site[] {
    const client = this.userInfo?.clients.find((client) => client.id === id);
    if (!client) {
      return [];
    }

    const siteAdminSiteIds =
      this.userInfo?.userSites
        .filter((userSite) => userSite.role?.databaseId === role)
        .map((userSite) => userSite.site?.id) || [];

    return client.sites?.filter((site) => siteAdminSiteIds.includes(site.id)) || [];
  }

  public notRoles(roles: RolesEnum[]): RolesEnum[] {
    return this.userRolesList.filter((role: RolesEnum) => !roles.includes(role));
  }
}
